export class HarmonyUtils {
    /**
     * Finds the closest MIDI note in the provided scale to the target MIDI note.
     * Useful for snapping chromatic notes to scale or finding nearest neighbors.
     */
    static findClosestInScale(scaleNotes: number[], targetMidi: number): number {
        if (scaleNotes.length === 0) return targetMidi; // Fallback

        let closest = scaleNotes[0];
        let minDiff = Math.abs(scaleNotes[0] - targetMidi);

        for (const note of scaleNotes) {
            const diff = Math.abs(note - targetMidi);
            if (diff < minDiff) {
                minDiff = diff;
                closest = note;
            }
        }
        return closest;
    }

    /**
     * Finds the index in scaleNotes for a specific scale degree (1-based) within a target octave offset relative to middle C (Octave 4).
     * @param scaleNotes Sorted array of MIDI notes in the current scale context
     * @param degree The 1-based degree (e.g. 1 for root, 3 for third)
     * @param octaveOffset Offset from Octave 4 (0 = Octave 4, -1 = Octave 3)
     */
    static findScaleIndexForDegree(scaleNotes: number[], degree: number, octaveOffset: number): number {
        const targetOctave = 4 + octaveOffset;
        // Filter notes that fall into this octave roughly
        // Note: dividing by 12 gives octave index. 60 is C4. 
        const octaveNotes = scaleNotes.filter(n => Math.floor(n / 12) === targetOctave);

        if (octaveNotes.length === 0) {
            // Fallback: Use the middle of the full scale
            return Math.floor(scaleNotes.length / 2);
        }

        // Map degree to index: Degree 1 -> Index 0
        const degreeIndex = (degree - 1) % octaveNotes.length;

        // Find the note
        const targetNote = octaveNotes[degreeIndex];

        // Return global index in scaleNotes
        const globalIndex = scaleNotes.indexOf(targetNote);

        return globalIndex !== -1 ? globalIndex : Math.floor(scaleNotes.length / 2);
    }

    /**
     * Similar to findScaleIndexForDegree but returns the MIDI Note number directly.
     */
    static findRootMidi(scaleNotes: number[], degree: number, octaveOffset: number): number {
        const index = this.findScaleIndexForDegree(scaleNotes, degree, octaveOffset);
        if (index >= 0 && index < scaleNotes.length) {
            return scaleNotes[index];
        }
        return 60; // Safety C4
    }

    /**
     * Finds shadow notes (lower harmony) for a given melody note based on the current chord context.
     * Used for "Auto-Divisi" or "Shadow Harmonizer".
     */
    static getShadowNotes(melodyNote: number, chordNotes: number[], count: number): number[] {
        if (chordNotes.length === 0) return [];
        let shadows: number[] = [];

        // Sort chord notes ascending and get pitch classes (0-11)
        const baseChordPCs = chordNotes.map(n => n % 12).sort((a, b) => a - b);

        let currentPitch = melodyNote - 1;
        let found = 0;
        let safety = 0;

        // Search downwards chromatically
        while (found < count && safety < 24) { // Check 2 octaves down max
            const pc = currentPitch % 12;
            if (baseChordPCs.includes(pc)) {
                shadows.push(currentPitch);
                found++;
            }
            currentPitch--;
            safety++;
        }

        return shadows;
    }

    /**
     * Transposes a note string by a given number of semitones.
     * Handles wrapping around octaves.
     * @param note Note name (e.g., 'C', 'F#', 'Bb' - converts Bb to A#)
     * @param semitones Number of semitones to shift (can be negative)
     */
    static transposeNote(note: string, semitones: number): string {
        const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

        // Normalize input (handle Bb -> A#, Eb -> D#, etc.)
        const ALIAS_MAP: Record<string, string> = {
            'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#',
            'Cb': 'B', 'Fb': 'E', 'E#': 'F', 'B#': 'C'
        };

        const normalized = ALIAS_MAP[note] || note;
        const index = NOTES.indexOf(normalized);

        if (index === -1) return note; // Fallback

        // Calculate new index with wrap-around
        let newIndex = (index + semitones) % 12;
        if (newIndex < 0) newIndex += 12;

        return NOTES[newIndex];
    }
}
